/**
 * anyonicBraidingPipeline — 2D topological braiding of anyons.
 *
 * Anyons are 2D particles with unusual exchange statistics. When
 * two anyons swap places, the system wavefunction acquires a
 * phase factor e^(i*pi*theta) where theta is neither 0 (boson) nor
 * 1 (fermion). When multiple anyons are braided around each other,
 * the worldlines form a braid with topological properties.
 *
 * Innovations:
 *   - 4 anyons move in a 2D plane, their worldlines traced as
 *     parametric curves.
 *   - The anyons are drawn as colored dots that follow the
 *     worldlines.
 *   - The "phase" of the system is visualized as a color cycle
 *     along the worldlines.
 *   - The braid crossing points are highlighted with bright
 *     spots.
 *   - Audio: bass drives the braiding speed, mid controls the
 *     braiding complexity, high drives the phase visualization.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface AnyonicBraidingPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

vec2 anyonPos(float i, float t, float bass) {
  float orbitT = t * (0.5 + bass * 0.5);
  float phase = i * 1.5708;
  float r = 0.30 + 0.05 * sin(orbitT * 0.3 + i);
  float r2 = 0.25 + 0.05 * cos(orbitT * 0.3 + i);
  float swapPhase = sin(orbitT * 0.5);
  float swapAmt = max(0.0, swapPhase);
  vec2 orbit = vec2(
    r * cos(orbitT + phase) * (1.0 - swapAmt * 0.5),
    r2 * sin(orbitT * 1.3 + phase) * (1.0 - swapAmt * 0.5)
  );
  float cross = 0.05 * sin(orbitT * 2.0 + i * 0.7);
  return orbit + vec2(cross, -cross * 0.7);
}

vec3 anyonColor(int i) {
  if (i == 0) return vec3(0.95, 0.45, 0.30);
  if (i == 1) return vec3(0.40, 0.85, 0.95);
  if (i == 2) return vec3(0.55, 0.95, 0.45);
  return vec3(0.95, 0.85, 0.45);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  float r = length(p);
  vec3 bg = mix(
    vec3(0.04, 0.06, 0.12),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  vec3 col = bg;

  for (int i = 0; i < 4; i++) {
    float fi = float(i);
    float worldline = 0.0;
    for (int k = 0; k < 40; k++) {
      float fk = float(k);
      float pastT = uTime - fk * 0.05 * (0.5 + high * 0.5);
      vec2 pos = anyonPos(fi, pastT, bass);
      float d = length(p - pos);
      float brightness = exp(-d * 300.0) * (1.0 - fk / 40.0);
      worldline += brightness;
    }
    col += anyonColor(i) * worldline * (0.5 + 0.3 * mid);
  }

  for (int i = 0; i < 4; i++) {
    float fi = float(i);
    vec2 pos = anyonPos(fi, uTime, bass);
    float d = length(p - pos);
    float core = exp(-d * 250.0);
    float halo = exp(-d * 30.0);
    col += anyonColor(i) * core * 1.5;
    col += vec3(1.0, 0.95, 0.7) * core * 0.8;
    col += anyonColor(i) * halo * 0.3;
  }

  for (int i = 0; i < 4; i++) {
    for (int j = i + 1; j < 4; j++) {
      vec2 pi = anyonPos(float(i), uTime, bass);
      vec2 pj = anyonPos(float(j), uTime, bass);
      float dd = length(pi - pj);
      if (dd < 0.08) {
        vec2 mid = (pi + pj) * 0.5;
        float d = length(p - mid);
        float bright = exp(-d * 100.0) * (1.0 - dd / 0.08);
        col += vec3(1.0, 0.95, 0.7) * bright * 0.8;
      }
    }
  }

  float phase = fract(uTime * 0.1);
  vec2 phasePos = vec2(0.78, 0.45);
  float phaseD = length(p - phasePos);
  float phaseRing = 1.0 - smoothstep(0.03, 0.05, abs(phaseD - 0.04));
  col += vec3(0.95, 0.75, 0.45) * phaseRing * (0.5 + 0.5 * sin(phase * 6.28));
  vec2 phaseDot = phasePos + vec2(cos(phase * 6.28), sin(phase * 6.28)) * 0.04;
  float dotD = length(p - phaseDot);
  float dotMask = 1.0 - smoothstep(0.0, 0.008, dotD);
  col += vec3(1.0, 0.95, 0.7) * dotMask * 1.2;

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeAnyonicBraidingBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeAnyonicBraidingRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createAnyonicBraidingPipeline(gl: WebGL2RenderingContext): AnyonicBraidingPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawAnyonicBraiding(
  gl: WebGL2RenderingContext,
  pipe: AnyonicBraidingPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
